/**
 * RoomCard Component
 * Displays individual room information in a card format
 */

import { Bed, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

const RoomCard = ({ room, isSelected, onClick }) => {
  const {
    id,
    name,
    roomNumber,
    roomType,
    capacity,
    price,
    available,
    image,
    images,
    beds,
    availableBeds,
    occupiedBeds,
  } = room;

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const imageList = images && images.length > 0 ? images : (image ? [image] : []);
  const currentImage = imageList[currentImageIndex];

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
  };

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-lg overflow-hidden transition-all duration-300 transform hover:scale-105 ${
        isSelected
          ? 'ring-2 ring-green-400 shadow-lg shadow-green-400/50'
          : 'hover:shadow-lg'
      } bg-gray-800 border border-gray-700`}
    >
      {/* Room Image Gallery */}
      <div className="relative h-48 bg-gray-700 overflow-hidden group">
        {currentImage ? (
          <img
            src={currentImage}
            alt={`Room ${roomNumber}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Bed className="w-12 h-12 text-gray-500" />
          </div>
        )}

        {/* Image Navigation */}
        {imageList.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Image Counter */}
            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
              {currentImageIndex + 1}/{imageList.length}
            </div>
          </>
        )}

        {available ? (
          <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
            Available
          </div>
        ) : (
          <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
            Full
          </div>
        )}
      </div>

      {/* Room Details */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="text-lg font-bold text-white">Room {roomNumber}</h3>
          <p className="text-sm text-gray-400">{roomType}</p>
        </div>

        {/* Room Stats */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2 text-gray-300">
            <Bed className="w-4 h-4 text-green-400" />
            <span>{beds?.length || capacity} beds</span>
          </div>
          <div className="flex items-center gap-2 text-gray-300">
            <Users className="w-4 h-4 text-green-400" />
            <span>{availableBeds} available</span>
          </div>
        </div>

        {/* Price */}
        <div className="text-lg font-bold text-green-400">
          Rs. {price}/month
        </div>

        {/* Bed Status */}
        <div className="pt-2 border-t border-gray-700">
          <div className="flex justify-between text-xs text-gray-400">
            <span>Occupied: {occupiedBeds}</span>
            <span>Available: {availableBeds}</span>
          </div>
          <div className="mt-2 w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full"
              style={{ width: `${(availableBeds / (beds?.length || capacity)) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomCard;
